
(() => {
  'use strict';
  const config = window.EIGHT_TEN || {};
  const root = new URL(config.root || '/', window.location.origin);
  if (root.origin !== window.location.origin) return;
  if (!root.pathname.endsWith('/')) root.pathname += '/';
  const route = path => new URL(path.replace(/^\//, ''), root).toString();
  const cartURL = new URL(config.cart || route('cart'), window.location.origin).toString();
  const esc = value => String(value == null ? '' : value).replace(/[&<>"']/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]));
  const displayTitle = value => String(value || '').replace(/\s*\*\*PREORDER\s*\*\*/gi,'').replace(/\b(SET|SKIRT)\b/g,word => word[0] + word.slice(1).toLowerCase()).trim();
  const selectors = scope => scope || document;
  const all = (query, scope) => Array.from(selectors(scope).querySelectorAll(query));
  const handleList = value => Array.isArray(value) ? [...new Set(value.filter(handle => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(handle)))].slice(0,100) : [];
  const read = key => { try { return handleList(JSON.parse(localStorage.getItem(key) || '[]')); } catch (_) { return []; } };
  const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {} };
  const savedKey = 'eight-ten-shopify-saved', recentKey = 'eight-ten-shopify-recent';
  let saved = read(savedKey), recent = read(recentKey), returningFocus = null;
  let currentCurrency = config.shopCurrency || 'USD', dialogRevision = 0, dialogKind = '', cartMutationPending = false;
  const money = cents => {
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:currentCurrency}).format(Number(cents || 0) / 100); }
    catch (_) { return (Number(cents || 0) / 100).toFixed(2) + ' ' + currentCurrency; }
  };
  const sameOriginURL = value => {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('Please open this piece in the shop.');
    return url;
  };
  const productURL = handle => route('products/' + encodeURIComponent(handle));
  const quickImageSizes = '(max-width:650px) 112px, 145px';
  function responsiveImage(value, options = {}) {
    const image = value && typeof value === 'object' ? value : {src:value};
    const src = image.src || image.url || '';
    const width = Math.floor(Number(options.width || image.width) || 0);
    const height = Math.floor(Number(options.height || image.height) || 0);
    const result = {src,srcset:'',sizes:options.sizes || '',width:width > 0 ? width : 0,height:height > 0 ? height : 0};
    if (!src) return result;
    try {
      const url = new URL(src,window.location.origin);
      if (!['http:','https:'].includes(url.protocol)) return {...result,src:''};
      // Only advertise resized widths for Shopify's image CDN, never arbitrary images.
      if (url.hostname !== 'cdn.shopify.com' && !url.pathname.startsWith('/cdn/shop/')) return result;
      const widths = [...new Set((options.candidates || [320,480,640,768,1000,1200,1400,1600,1800]).map(size => Math.min(size,width > 0 ? width : size)))].filter(size => size > 0).sort((a,b) => a - b);
      const atWidth = size => { const resized = new URL(url); resized.searchParams.set('width',String(size)); return resized.toString(); };
      result.src = atWidth(Math.min(options.fallback || 1200,width > 0 ? width : options.fallback || 1200));
      result.srcset = widths.map(size => atWidth(size) + ' ' + size + 'w').join(', ');
    } catch (_) { /* Native image URLs remain usable if resizing is unavailable. */ }
    return result;
  }
  function imageAttributes(image) {
    return ' src="' + esc(image.src) + '"' + (image.srcset ? ' srcset="' + esc(image.srcset) + '" sizes="' + esc(image.sizes) + '"' : '');
  }
  function applyImage(node, image) {
    node.src = image.src;
    node.srcset = image.srcset;
    node.sizes = image.sizes;
    if (image.width && image.height) { node.width = image.width; node.height = image.height; }
  }
  function productImageDetails(product, value) {
    const src = typeof value === 'string' ? value : value && (value.src || value.url);
    if (!src) return value;
    const previews = (product.media || []).map(media => media.preview_image).filter(Boolean);
    const match = previews.find(image => {
      try { return new URL(image.src || image.url,window.location.origin).pathname === new URL(src,window.location.origin).pathname; } catch (_) { return false; }
    });
    return match ? {...match,src} : value;
  }
  const purchaseStatuses = new Map();
  const productTags = product => (Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',')).map(tag => String(tag).trim().toLowerCase());
  const isPreorderProduct = product => /pre-?order/i.test(product.product_title || product.title || '') || productTags(product).some(tag => tag === 'preorder' || tag === 'pre-order');
  const needsProductPage = product => isPreorderProduct(product) || product.available === false || product.requires_selling_plan === true || Boolean(product.selling_plan_groups && product.selling_plan_groups.length) || productTags(product).includes('requires-product-page');
  function readPurchaseStatuses(scope) {
    all('[data-product-id][data-preorder],[data-cart-product-id][data-preorder]',scope).forEach(node => {
      const id = node.dataset.cartProductId || node.dataset.productId;
      if (/^\d+$/.test(id || '')) purchaseStatuses.set(String(id),{preorder:node.dataset.preorder === 'true',notice:node.dataset.preorderNotice || ''});
    });
  }
  async function loadCartPurchaseStatuses(cart) {
    // Shopify's Ajax cart omits product metafields and tags. Read the native cart's
    // Liquid-rendered status for any product not already rendered on this page.
    if (!(cart.items || []).some(item => !purchaseStatuses.has(String(item.product_id)))) return cart;
    try {
      const response = await fetch(cartURL,{credentials:'same-origin',headers:{Accept:'text/html'}});
      if (!response.ok) throw new Error('The bag details could not be loaded.');
      const page = new DOMParser().parseFromString(await response.text(),'text/html');
      readPurchaseStatuses(page);
    } catch (_) { /* Keep native cart access available if its optional preview lookup fails. */ }
    return cart;
  }
  function itemPurchaseStatus(item) {
    const status = purchaseStatuses.get(String(item.product_id));
    return {preorder:Boolean(status && status.preorder) || isPreorderProduct(item),notice:status && status.notice || '',verified:Boolean(status)};
  }
  const liveRegion = () => document.querySelector('[data-store-status]');
  function announce(message) { const node = liveRegion(); if (node) node.textContent = message; }
  function errorNode(scope) {
    let node = scope.querySelector('[data-form-error]');
    if (!node) { node = document.createElement('p'); node.dataset.formError = ''; node.className = 'selection-error'; node.setAttribute('role','alert'); scope.appendChild(node); }
    return node;
  }
  function showError(scope, message) { const node = errorNode(scope); node.textContent = message; node.hidden = false; }
  function clearError(scope) { const node = scope.querySelector('[data-form-error]'); if (node) { node.textContent = ''; node.hidden = true; } }
  const dialog = () => document.getElementById('shop-dialog');
  function closeDialog() { const modal = dialog(); if (modal && modal.open) modal.close(); }
  function setCartBusy(modal,busy) {
    if (!modal) return;
    modal.setAttribute('aria-busy',String(busy));
    all('[data-cart-line],button[name="checkout"]',modal).forEach(node => { node.disabled = busy; });
  }
  function restoreCartFocus(modal,line,action) {
    const controls = all('[data-cart-line]',modal);
    const target = controls.find(node => node.dataset.cartLine === line && node.dataset.cartAction === action) || controls[0] || modal.querySelector('[name="checkout"]') || modal.querySelector('[data-close-dialog]');
    target?.focus();
  }
  function openDialog(html,kind = 'product') {
    const modal = dialog();
    if (!modal || typeof modal.showModal !== 'function') return false;
    if (!modal.open) returningFocus = document.activeElement;
    const content = modal.querySelector('[data-dialog-content]') || modal;
    content.innerHTML = html;
    dialogKind = kind;
    dialogRevision++;
    if (!modal.open) modal.showModal();
    document.body.style.overflow = 'hidden';
    const focus = modal.querySelector('[data-close-dialog]');
    if (focus) focus.focus();
    syncSaved(modal);
    if (cartMutationPending && modal.querySelector('[name="checkout"]')) setCartBusy(modal,true);
    return true;
  }
  const closeButton = '<button class="icon-button" type="button" data-close-dialog aria-label="Close dialog"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></button>';
  const shell = (heading, html) => '<div class="dialog-head"><h2 id="shop-dialog-title">' + esc(heading) + '</h2>' + closeButton + '</div><div class="dialog-body">' + html + '</div>';
  async function jsonRequest(url, data) {
    const response = await fetch(url, {method:data ? 'POST' : 'GET',credentials:'same-origin',headers:{Accept:'application/json',...(data ? {'Content-Type':'application/json'} : {})},...(data ? {body:JSON.stringify(data)} : {})});
    let body;
    try { body = await response.json(); } catch (_) { throw new Error('The shop could not complete that request. Please try again.'); }
    if (!response.ok) throw new Error(typeof body.description === 'string' ? body.description : typeof body.message === 'string' ? body.message : 'That option could not be added. Please review your bag.');
    return body;
  }
  function syncCartCount(cart) {
    if (cart.currency) currentCurrency = cart.currency;
    all('[data-cart-count]').forEach(node => { node.textContent = cart.item_count || 0; node.hidden = !cart.item_count; });
    all('[data-open-cart]').forEach(node => node.setAttribute('aria-label','Open shopping bag, ' + (cart.item_count || 0) + ' items'));
  }
  function safeProductLink(value) {
    try { return sameOriginURL(value).toString(); } catch (_) { return cartURL; }
  }
  function cartMarkup(cart) {
    if (!cart.items || !cart.items.length) return shell('Your bag', '<div class="empty"><h3>A little room for something you love.</h3><p>Your bag is empty.</p><a class="button" href="' + esc(route('collections/all')) + '">Explore the collection</a></div>');
    const items = cart.items.map(item => {
      const image = responsiveImage(item.featured_image || item.image,{candidates:[76,88,152,176,228,264],fallback:176,sizes:'(max-width:650px) 76px, 88px'});
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? '<p>' + esc(item.variant_title) + '</p>' : '';
      const unit = item.final_price == null ? item.price : item.final_price;
      const lineTotal = item.final_line_price == null ? unit * item.quantity : item.final_line_price;
      const title = displayTitle(item.product_title || item.title);
      const purchase = itemPurchaseStatus(item);
      const preorder = purchase.preorder ? '<p class="detail-note"><strong>Preorder</strong> — ' + (purchase.notice ? esc(purchase.notice) + ' ' : '') + '<a class="text-link" href="' + esc(safeProductLink(item.url)) + '">Review timing and shipping details</a>.</p>' : '';
      const properties = Object.entries(item.properties || {}).filter(([name,value]) => name.charAt(0) !== '_' && value).map(([name,value]) => '<p class="bag-property">' + esc(name) + ': ' + esc(value) + '</p>').join('');
      return '<div class="bag-item">' + (image.src ? '<img' + imageAttributes(image) + ' alt="' + esc(title) + '" width="88" height="116" loading="lazy" decoding="async">' : '') + '<div><h3><a href="' + esc(safeProductLink(item.url)) + '">' + esc(title) + '</a></h3>' + variant + preorder + properties + '<p>' + money(unit) + '</p><div class="bag-item-controls"><div class="quantity"><button type="button" data-cart-line="' + esc(item.key) + '" data-cart-action="decrease" data-cart-quantity="' + Math.max(0,item.quantity - 1) + '" aria-label="Reduce quantity of ' + esc(title) + '">−</button><output>' + item.quantity + '</output><button type="button" data-cart-line="' + esc(item.key) + '" data-cart-action="increase" data-cart-quantity="' + (item.quantity + 1) + '" aria-label="Increase quantity of ' + esc(title) + '">+</button></div><button type="button" class="remove" data-cart-line="' + esc(item.key) + '" data-cart-action="remove" data-cart-quantity="0">Remove</button></div><p class="bag-line-total">' + money(lineTotal) + ' line total</p></div></div>';
    }).join('');
    const includesPreorder = cart.items.some(item => itemPurchaseStatus(item).preorder);
    const preorderSummary = includesPreorder ? '<div class="detail-alert"><p>Your bag includes a preorder. If you are ordering ready-to-ship pieces with a preorder, review the preorder description for how your order will ship.</p></div>' : cart.items.some(item => !itemPurchaseStatus(item).verified) ? '<p>Review product timing and shipping details in your bag before checkout.</p>' : '';
    return shell('Your bag', '<p data-form-error class="selection-error" role="alert" hidden></p>' + preorderSummary + items + '<div class="bag-summary"><div class="subtotal"><span>Subtotal</span><strong>' + money(cart.total_price) + '</strong></div>' + (cart.total_discount > 0 ? '<p>Discounts applied: ' + money(cart.total_discount) + '</p>' : '') + '<p>Taxes and shipping are calculated at checkout.</p><form action="' + esc(cartURL) + '" method="post"><button type="submit" class="button full" name="checkout">Checkout</button></form><a class="text-link" href="' + esc(cartURL) + '">View your bag</a><button type="button" class="text-link" data-close-dialog>Keep exploring</button></div>');
  }
  async function openCart() {
    const modal = dialog();
    if (!modal || typeof modal.showModal !== 'function') { window.location.assign(cartURL); return; }
    openDialog(shell('Your bag', '<p role="status">Opening your bag…</p>'),'cart');
    const requestRevision = dialogRevision;
    try { const cart = await jsonRequest(route('cart.js')); await loadCartPurchaseStatuses(cart); syncCartCount(cart); if (modal.open && requestRevision === dialogRevision) openDialog(cartMarkup(cart),'cart'); }
    catch (_) { if (modal.open && requestRevision === dialogRevision) window.location.assign(cartURL); }
  }
  function variantData(form) {
    const script = form.querySelector('script[data-variants]') || form.closest('[data-product-section]')?.querySelector('script[data-variants]');
    if (!script) return null;
    try { const data = JSON.parse(script.textContent); return Array.isArray(data) ? data : data.variants || null; } catch (_) { return null; }
  }
  function selectedVariant(form) {
    const field = form.querySelector('[name="id"]'), variants = variantData(form);
    return field && field.value && variants ? variants.find(variant => String(variant.id) === String(field.value)) : null;
  }
  function syncVariant(form, options = {}) {
    const select = form.querySelector('[name="id"]');
    if (!select) return;
    const variant = selectedVariant(form);
    const unavailable = Boolean(select.disabled) || (variant ? !variant.available : Boolean(select.selectedOptions && select.selectedOptions[0] && select.selectedOptions[0].disabled));
    const valid = Boolean(select.value) && !unavailable;
    const section = form.closest('[data-product-section]') || form.closest('dialog') || form;
    const purchaseLabel = form.dataset.preorder === 'true' || section.dataset?.preorder === 'true' ? 'Preorder' : 'Add to bag';
    all('[data-variant-choice]', form.closest('[data-product-section]') || form).forEach(button => button.setAttribute('aria-pressed',String(button.dataset.variantChoice === select.value)));
    all('[type="submit"]', form).forEach(button => {
      button.disabled = !valid;
      if (button.hasAttribute('data-add-label') || button.hasAttribute('data-add-button')) button.textContent = unavailable ? 'Sold out' : valid ? purchaseLabel : 'Choose an option';
    });
    all('[data-sticky-purchase]', section).forEach(button => { button.disabled = unavailable; button.textContent = unavailable ? 'Sold out' : valid ? purchaseLabel : 'Choose an option'; });
    if (variant) {
      all('[data-variant-price]', section).forEach(node => node.textContent = money(variant.price));
      if (options.userChanged && variant.featured_image) {
        const image = variant.featured_image, mediaId = variant.featured_media && variant.featured_media.id;
        const sources = all('[data-gallery-source]',section);
        const source = sources.find(node => mediaId && node.dataset.mediaId === String(mediaId) || node.dataset.imageId === String(image.id)) || sources.find(node => {
          try { return new URL(node.dataset.src,window.location.origin).pathname === new URL(image.src,window.location.origin).pathname; } catch (_) { return false; }
        });
        if (source) chooseGallery(source);
        else {
          const quickImage = section.querySelector('[data-quick-image]');
          if (quickImage) {
            applyImage(quickImage,responsiveImage(image,{candidates:[112,145,224,290,336,435],fallback:290,sizes:quickImageSizes}));
            quickImage.alt = [quickImage.dataset.productTitle,variant.title === 'Default Title' ? '' : variant.title].filter(Boolean).join(' — ') || quickImage.alt;
          }
        }
      }
      const page = new URL(window.location.href);
      if (options.userChanged && section.hasAttribute('data-current-product') && !form.closest('dialog')) { page.searchParams.set('variant',variant.id); history.replaceState({},'',page); }
    }
  }
  async function addItems(items, scope) {
    const requestRevision = dialogRevision;
    clearError(scope);
    await jsonRequest(route('cart/add.js'), {items});
    announce('Added to your bag.');
    try { const cart = await jsonRequest(route('cart.js')); await loadCartPurchaseStatuses(cart); syncCartCount(cart); if (requestRevision !== dialogRevision && dialogKind !== 'cart') return; if (!openDialog(cartMarkup(cart),'cart')) window.location.assign(cartURL); }
    catch (_) { if (requestRevision === dialogRevision || dialogKind === 'cart') window.location.assign(cartURL); }
  }
  async function submitProduct(form) {
    if (form.dataset.busy === 'true') return;
    clearError(form);
    const variantField = form.querySelector('[name="id"]');
    const variant = selectedVariant(form);
    if (!variantField || variantField.disabled || !/^\d+$/.test(variantField.value) || variant && !variant.available) { showError(form,'Choose an available option first.'); variantField?.focus(); return; }
    if (!form.reportValidity()) return;
    const data = new FormData(form);
    const quantity = Math.max(1,parseInt(data.get('quantity') || '1',10));
    const properties = {};
    for (const [name,value] of data.entries()) { const match = name.match(/^properties\[(.+)\]$/); if (match && typeof value === 'string') properties[match[1]] = value; }
    const item = {id:variantField.value,quantity,...(Object.keys(properties).length ? {properties} : {})};
    const sellingPlan = data.get('selling_plan'); if (sellingPlan) item.selling_plan = sellingPlan;
    form.dataset.busy = 'true';
    const buttons = all('[type="submit"]',form);
    buttons.forEach(button => { button.disabled = true; });
    try { await addItems([item],form); }
    catch (error) { showError(form,error.message); }
    finally { delete form.dataset.busy; syncVariant(form); }
  }
  async function quickShop(value, fullDetails = false) {
    const url = sameOriginURL(value);
    if (fullDetails) { window.location.assign(url.toString()); return; }
    const endpoint = new URL(url); endpoint.search = ''; endpoint.hash = ''; endpoint.pathname = endpoint.pathname.replace(/\/$/,'') + '.js';
    if (!openDialog(shell('A closer look','<p role="status">Opening this piece…</p>'))) { window.location.assign(url); return; }
    const requestRevision = dialogRevision;
    try {
      const product = await jsonRequest(endpoint.toString());
      if (!dialog().open || requestRevision !== dialogRevision) return;
      if (needsProductPage(product)) { closeDialog(); window.location.assign(url.toString()); return; }
      const variants = product.variants || [], single = variants.length === 1;
      const image = responsiveImage(productImageDetails(product,single && variants[0]?.featured_image || product.featured_image || product.images && product.images[0]),{candidates:[112,145,224,290,336,435],fallback:290,sizes:quickImageSizes});
      const title = displayTitle(product.title);
      const options = (single ? '' : '<option value="">Choose an option</option>') + variants.map(variant => '<option value="' + variant.id + '"' + (!variant.available ? ' disabled' : '') + (single && variant.available ? ' selected' : '') + '>' + esc(variant.title === 'Default Title' ? 'One option' : variant.title) + ' — ' + money(variant.price) + (variant.available ? '' : ' · Sold out') + '</option>').join('');
      const json = JSON.stringify(product).replace(/</g,'\\u003c');
      openDialog(shell('A closer look','<div class="quick-shop-top">' + (image.src ? '<img data-quick-image data-product-title="' + esc(title) + '"' + imageAttributes(image) + ' alt="' + esc(title) + '" width="' + (image.width || 145) + '" height="' + (image.height || 193) + '" decoding="async">' : '') + '<div><h3>' + esc(title) + '</h3><p data-variant-price>' + money(product.price) + '</p><a class="text-link" href="' + esc(url.toString()) + '">Full details</a></div></div><form action="' + esc(route('cart/add')) + '" method="post" data-product-form><label class="variant-title" for="QuickVariant">Choose an option</label><select id="QuickVariant" name="id" required>' + options + '</select><label for="QuickQuantity">Quantity</label><input id="QuickQuantity" type="number" name="quantity" value="1" min="1" inputmode="numeric"><button class="button full" type="submit" data-add-label' + (!single || !variants[0]?.available ? ' disabled' : '') + '>Add to bag</button><p data-form-error class="selection-error" role="alert" hidden></p><script type="application/json" data-variants>' + json + '</script></form>'));
      const form = dialog().querySelector('[data-product-form]');
      if (form) syncVariant(form);
    } catch (_) {
      if (!dialog().open || requestRevision !== dialogRevision) return;
      openDialog(shell('A closer look','<p>This piece is easier to view on its product page.</p><a class="button" href="' + esc(url.toString()) + '">View full details</a>'));
    }
  }
  function syncSaved(scope) {
    all('[data-save-handle]',scope).forEach(button => {
      const active = saved.includes(button.dataset.saveHandle);
      button.setAttribute('aria-pressed',String(active));
      const title = button.dataset.saveTitle || displayTitle(button.closest('[data-product-card]')?.querySelector('h3')?.textContent || button.closest('[data-current-product]')?.querySelector('h1')?.textContent || button.getAttribute('aria-label')?.replace(/^Save /i,'') || button.dataset.saveHandle.replace(/-/g,' '));
      button.dataset.saveTitle = title;
      button.setAttribute('aria-label',active ? 'Remove ' + title + ' from saved' : 'Save ' + title);
    });
    all('[data-saved-count]').forEach(node => { node.textContent = saved.length; node.hidden = !saved.length; });
  }
  function cardContainerState(container, hasCards, hasHandles = false) {
    const section = container.closest('section');
    const savedEmpty = section && section.querySelector('[data-saved-empty]');
    if (savedEmpty) savedEmpty.hidden = hasCards || hasHandles;
    const recentSection = container.closest('[data-recent-section]');
    if (recentSection) recentSection.hidden = !hasCards;
  }
  async function populateCards(container, handles) {
    const requestToken = String(Date.now()) + Math.random();
    container.dataset.cardRequest = requestToken;
    container.dataset.loading = 'true';
    const available = handleList(handles).filter(handle => handle !== container.dataset.exclude).slice(0,container.hasAttribute('data-recent-products') ? 4 : 100);
    container.replaceChildren();
    cardContainerState(container,false,available.length > 0);
    if (!available.length) {
      const savedEmpty = container.closest('section')?.querySelector('[data-saved-empty]');
      if (!savedEmpty && !container.hasAttribute('data-recent-products')) {
        container.innerHTML = '<div class="empty"><h2>A little room for something you love.</h2><p>Tap the heart on a piece to save it here.</p><a class="button" href="' + esc(route('collections/all')) + '">Explore the collection</a></div>';
      }
      delete container.dataset.loading;
      return;
    }
    const results = await Promise.all(available.map(async handle => {
      try {
        const response = await fetch(productURL(handle) + '?view=card',{credentials:'same-origin'});
        if (!response.ok) return null;
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html,'text/html');
        const card = parsed.querySelector('.product-card');
        return card ? document.importNode(card,true) : null;
      } catch (_) { return null; }
    }));
    if (!container.isConnected || container.dataset.cardRequest !== requestToken) return;
    const cards = results.filter(Boolean);
    cards.forEach(card => container.appendChild(card));
    cardContainerState(container,cards.length > 0,available.length > 0);
    if (!cards.length && container.hasAttribute('data-saved-products')) container.innerHTML = '<p>Your saved pieces may have changed. <a href="' + esc(route('collections/all')) + '">Browse the collection</a>.</p>';
    delete container.dataset.loading;
    readPurchaseStatuses(container);
    syncSaved(container);
  }
  async function loadRecommendations(section) {
    if (!section.dataset.productId || !section.dataset.sectionId || section.dataset.recommendationsLoading === 'true') return;
    const grid = section.querySelector('[data-recommendation-grid]');
    if (!grid) return;
    if (grid.querySelector('.product-card')) { section.hidden = false; return; }
    section.hidden = true;
    section.dataset.recommendationsLoading = 'true';
    const requestToken = String(Date.now()) + Math.random();
    section.dataset.recommendationsRequest = requestToken;
    try {
      const url = sameOriginURL(config.productRecommendations || route('recommendations/products'));
      url.searchParams.set('section_id',section.dataset.sectionId);
      url.searchParams.set('product_id',section.dataset.productId);
      url.searchParams.set('limit','4');
      url.searchParams.set('intent',section.dataset.intent === 'complementary' ? 'complementary' : 'related');
      const response = await fetch(url.toString(),{credentials:'same-origin'});
      if (!response.ok) throw new Error('Recommendations unavailable.');
      const parsed = new DOMParser().parseFromString(await response.text(),'text/html');
      const rendered = parsed.querySelector('[data-recommendation-grid]');
      const cards = rendered ? all('.product-card',rendered) : [];
      if (!section.isConnected || section.dataset.recommendationsRequest !== requestToken) return;
      grid.replaceChildren();
      cards.forEach(card => grid.appendChild(document.importNode(card,true)));
      section.hidden = cards.length === 0;
      readPurchaseStatuses(grid);
      syncSaved(grid);
    } catch (_) {
      if (section.isConnected && section.dataset.recommendationsRequest === requestToken) section.hidden = true;
    } finally {
      if (section.dataset.recommendationsRequest === requestToken) delete section.dataset.recommendationsLoading;
    }
  }
  function menuPairs() {
    return all('[data-menu-toggle]').map(button => ({button,panel:document.getElementById(button.getAttribute('aria-controls'))})).filter(pair => pair.panel);
  }
  function setMenu(pair,open,restoreFocus = false) {
    pair.panel.hidden = !open;
    pair.button.setAttribute('aria-expanded',String(open));
    pair.button.setAttribute('aria-label',open ? 'Close menu' : 'Open menu');
    if (!open && restoreFocus) pair.button.focus();
  }
  function chooseGallery(button) {
    const gallery = button.closest('[data-product-gallery],.gallery');
    const main = gallery && gallery.querySelector('[data-gallery-main]');
    if (!main) return;
    const src = button.dataset.src || button.getAttribute('data-gallery-source');
    if (src) {
      const image = responsiveImage(src,{width:button.dataset.imageWidth,height:button.dataset.imageHeight,sizes:button.dataset.sizes || '(max-width:650px) 100vw, 50vw'});
      if (button.dataset.srcset) image.srcset = button.dataset.srcset;
      applyImage(main,image);
    }
    main.alt = button.dataset.alt || '';
    all('[data-gallery-source]',gallery).forEach(item => item.setAttribute('aria-pressed',String(item === button)));
    const sources = all('[data-gallery-source]',gallery), index = sources.indexOf(button);
    const position = gallery.querySelector('[data-gallery-position]');
    if (position) position.textContent = (index + 1) + ' / ' + sources.length;
    const caption = gallery.querySelector('[data-gallery-caption]');
    if (caption) caption.textContent = button.dataset.caption || '';
  }
  function stepGallery(control,moveFocus = false) {
    const gallery = control.closest('[data-product-gallery],.gallery');
    if (!gallery) return;
    const sources = all('[data-gallery-source]',gallery);
    if (!sources.length) return;
    const index = Math.max(0,sources.findIndex(source => source.getAttribute('aria-pressed') === 'true'));
    const step = ['previous','prev','-1'].includes(control.dataset.galleryStep) ? -1 : 1;
    const selected = sources[(index + step + sources.length) % sources.length];
    chooseGallery(selected);
    if (moveFocus) { selected.focus({preventScroll:true}); selected.scrollIntoView({block:'nearest',inline:'nearest'}); }
  }
  function lookItems(form) {
    const pieces = all('[data-look-piece]',form);
    return pieces.map(piece => {
      const included = piece.querySelector('[data-look-include]');
      const field = piece.querySelector('[data-look-variant],select[name="ids[]"]');
      return {included:!included || !included.disabled && included.checked,field};
    }).filter(piece => piece.included);
  }
  function syncLook(form) {
    const pieces = lookItems(form);
    let total = 0;
    pieces.forEach(piece => { const option = piece.field && piece.field.selectedOptions[0]; if (option) total += Number(option.dataset.price || 0); });
    const node = form.querySelector('[data-look-total]'); if (node) node.textContent = money(total);
  }
  async function submitLook(form) {
    if (form.dataset.busy === 'true') return;
    const pieces = lookItems(form);
    if (!pieces.length) { showError(form,'Choose a piece to start your look.'); return; }
    const invalid = pieces.find(piece => !piece.field || !/^\d+$/.test(piece.field.value) || piece.field.disabled || piece.field.selectedOptions[0]?.disabled);
    if (invalid) { showError(form,'Choose an available option for each selected piece.'); invalid.field?.focus(); return; }
    const items = pieces.map(piece => ({id:piece.field.value,quantity:1}));
    const deduplicated = [];
    items.forEach(item => { const existing = deduplicated.find(line => line.id === item.id); if (existing) existing.quantity++; else deduplicated.push(item); });
    form.dataset.busy = 'true';
    const button = form.querySelector('[type="submit"]'); if (button) button.disabled = true;
    try { await addItems(deduplicated,form); }
    catch (error) { showError(form,error.message); }
    finally { delete form.dataset.busy; if (button) button.disabled = false; }
  }
  function initialize(scope = document) {
    readPurchaseStatuses(scope);
    syncSaved(scope);
    all('[data-product-form]',scope).forEach(syncVariant);
    all('[data-look-form]',scope).forEach(syncLook);
    all('[data-curated-tab]',scope).forEach(tab => { if (tab.getAttribute('role') === 'tab') tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1; });
    all('[data-product-gallery]',scope).forEach(gallery => { const sources = all('[data-gallery-source]',gallery),position = gallery.querySelector('[data-gallery-position]'); if (position && sources.length) position.textContent = (Math.max(0,sources.findIndex(source => source.getAttribute('aria-pressed') === 'true')) + 1) + ' / ' + sources.length; });
    all('[data-current-product]',scope).forEach(node => {
      const handle = node.dataset.handle;
      if (!handle || !handleList([handle]).length) return;
      recent = [handle,...recent.filter(item => item !== handle)].slice(0,8);
      write(recentKey,recent);
    });
    all('[data-saved-products]',scope).forEach(node => populateCards(node,saved));
    all('[data-recent-products]',scope).forEach(node => populateCards(node,recent));
    all('[data-recommendations]',scope).forEach(loadRecommendations);
  }
  document.addEventListener('click',async event => {
    const menuToggle = event.target.closest('[data-menu-toggle]');
    if (menuToggle) {
      event.preventDefault();
      const pair = menuPairs().find(pair => pair.button === menuToggle);
      if (pair) setMenu(pair,pair.panel.hidden);
      return;
    }
    menuPairs().forEach(pair => { if (!pair.panel.hidden && (!pair.panel.contains(event.target) || event.target.closest('a'))) setMenu(pair,false); });
    all('.desktop-nav details[open]').forEach(details => { if (!details.contains(event.target) || event.target.closest('a')) details.open = false; });
    const button = event.target.closest('button,a');
    if (!button) return;
    if (button.hasAttribute('data-close-dialog')) { event.preventDefault(); closeDialog(); return; }
    if (button.hasAttribute('data-open-cart')) { event.preventDefault(); await openCart(); return; }
    if (button.hasAttribute('data-quick-shop')) { event.preventDefault(); try { await quickShop(button.dataset.productUrl || button.href,button.hasAttribute('data-full-details')); } catch (_) { if (button.href) window.location.assign(button.href); } return; }
    if (button.hasAttribute('data-save-handle')) {
      event.preventDefault();
      const handle = button.dataset.saveHandle;
      if (!handleList([handle]).length) return;
      const active = saved.includes(handle);
      saved = active ? saved.filter(item => item !== handle) : [handle,...saved].slice(0,100);
      write(savedKey,saved); syncSaved();
      all('[data-saved-products]').forEach(node => populateCards(node,saved));
      announce(active ? 'Removed from your saved pieces.' : 'Saved in this browser.');
      return;
    }
    if (button.hasAttribute('data-filter-toggle')) {
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (panel) { const open = !panel.classList.contains('is-open'); panel.classList.toggle('is-open',open); button.setAttribute('aria-expanded',String(open)); }
      return;
    }
    if (button.hasAttribute('data-curated-tab')) {
      const section = button.closest('[data-curated-section],.shopify-section,section') || document;
      const target = button.dataset.target || button.getAttribute('aria-controls');
      all('[data-curated-tab]',section).forEach(tab => { tab.setAttribute('aria-pressed',String(tab === button)); if (tab.getAttribute('role') === 'tab') tab.setAttribute('aria-selected',String(tab === button)); tab.classList.toggle('active',tab === button); if (tab.getAttribute('role') === 'tab') tab.tabIndex = tab === button ? 0 : -1; });
      all('[data-curated-panel]',section).forEach(panel => { panel.hidden = panel.id !== target; });
      return;
    }
    if (button.hasAttribute('data-gallery-source')) { chooseGallery(button); return; }
    if (button.hasAttribute('data-gallery-step')) { stepGallery(button); return; }
    if (button.hasAttribute('data-variant-choice')) {
      const section = button.closest('[data-product-section]') || document;
      const form = section.querySelector('[data-product-form]'), field = form && form.querySelector('[name="id"]');
      if (field && !button.disabled) { field.value = button.dataset.variantChoice; syncVariant(form,{userChanged:true}); }
      return;
    }
    if (button.hasAttribute('data-sticky-purchase')) {
      const form = button.dataset.formId ? document.getElementById(button.dataset.formId) : button.closest('[data-product-section]')?.querySelector('[data-product-form]') || document.querySelector('[data-product-form]');
      const field = form && form.querySelector('[name="id"]');
      if (!field || !field.value) { field?.scrollIntoView({behavior:'smooth',block:'center'}); field?.focus(); }
      else if (typeof form.requestSubmit === 'function') form.requestSubmit(); else form.querySelector('[type="submit"]')?.click();
      return;
    }
    if (button.hasAttribute('data-cart-line')) {
      const modal = dialog(), scope = modal && modal.querySelector('.dialog-body');
      if (!scope || cartMutationPending) return;
      const quantity = Number(button.dataset.cartQuantity);
      if (!Number.isInteger(quantity) || quantity < 0) return;
      const requestRevision = dialogRevision, line = button.dataset.cartLine, action = button.dataset.cartAction;
      cartMutationPending = true;
      setCartBusy(modal,true);
      try {
        const cart = await jsonRequest(route('cart/change.js'),{id:line,quantity});
        await loadCartPurchaseStatuses(cart);
        syncCartCount(cart);
        if (modal.open && (requestRevision === dialogRevision || dialogKind === 'cart')) {
          const restoreFocus = requestRevision === dialogRevision;
          openDialog(cartMarkup(cart),'cart');
          setCartBusy(modal,false);
          if (restoreFocus) restoreCartFocus(modal,line,action);
        }
        announce('Your bag has been updated.');
      } catch (error) {
        if (modal.open && requestRevision === dialogRevision) { showError(scope,error.message); setCartBusy(modal,false); button.focus(); }
      } finally { cartMutationPending = false; setCartBusy(modal,false); }

    }
  });
  document.addEventListener('submit',event => {
    const form = event.target;
    if (cartMutationPending && form.closest('#shop-dialog') && form.querySelector('[name="checkout"]')) { event.preventDefault(); announce('Updating your bag. Please wait before checking out.'); return; }
    if (form.matches('[data-product-form]')) { if (all('input[type="file"]',form).some(input => input.files && input.files.length)) return; event.preventDefault(); submitProduct(form); }
    else if (form.matches('[data-look-form]')) { event.preventDefault(); submitLook(form); }
  });
  document.addEventListener('change',event => {
    const form = event.target.closest('form');
    if (form && form.hasAttribute('data-product-form') && event.target.name === 'id') { clearError(form); syncVariant(form,{userChanged:true}); }
    if (form && form.hasAttribute('data-look-form')) { clearError(form); syncLook(form); }
  });
  document.addEventListener('keydown',event => {
    if (event.key === 'Escape') {
      menuPairs().forEach(pair => { if (!pair.panel.hidden) { event.preventDefault(); setMenu(pair,false,true); } });
      const details = event.target.closest('.desktop-nav details[open]');
      if (details) { event.preventDefault(); details.open = false; details.querySelector(':scope > summary')?.focus(); }
    }
    if (event.target.matches('[data-curated-tab][role="tab"]') && ['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) {
      const section = event.target.closest('[data-curated-section],.shopify-section,section') || document;
      const tabs = all('[data-curated-tab][role="tab"]',section),index = tabs.indexOf(event.target);
      const target = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowLeft' ? -1 : 1) + tabs.length) % tabs.length;
      if (tabs[target]) { event.preventDefault(); tabs[target].click(); tabs[target].focus(); }
      return;
    }
    if (!['ArrowLeft','ArrowRight'].includes(event.key) || !event.target.closest('[data-gallery-source]')) return;
    const gallery = event.target.closest('[data-product-gallery],.gallery');
    const control = gallery && gallery.querySelector('[data-gallery-step="' + (event.key === 'ArrowLeft' ? 'previous' : 'next') + '"]');
    if (control) { event.preventDefault(); stepGallery(control,true); }
  });
  const modal = dialog();
  if (modal) {
    modal.addEventListener('close',() => { dialogRevision++; dialogKind = ''; document.body.style.overflow = ''; if (returningFocus && returningFocus.isConnected) returningFocus.focus(); returningFocus = null; });
    modal.addEventListener('click',event => { if (event.target === modal) { const rect = modal.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(); } });
  }
  if (typeof window.matchMedia === 'function') {
    const mobile = window.matchMedia('(max-width:900px)');
    const closeDesktopMenu = event => { if (!event.matches) menuPairs().forEach(pair => setMenu(pair,false)); else all('.desktop-nav details[open]').forEach(details => { details.open = false; }); };
    if (typeof mobile.addEventListener === 'function') mobile.addEventListener('change',closeDesktopMenu);
  }
  document.addEventListener('shopify:section:load',event => initialize(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => initialize()); else initialize();
})();

